import snakeCase from 'lodash/snakeCase';
import decodeGzip from './decodeGzip';

let data;

/**
 * Converts a key to snake_case format.
 * If the key is an array, joins the snake_cased elements with underscores.
 *
 * @param {string|string[]} key - The key or array of keys to convert
 * @returns {string} The snake_cased key or joined keys
 * @example
 * getSnakeCaseKey('firstName') // returns 'first_name'
 * getSnakeCaseKey(['user', 'firstName']) // returns 'user_first_name'
 */
export const getSnakeCaseKey = (key) =>
  Array.isArray(key) ? key.map(snakeCase).join('_') : snakeCase(key);

/**
 * Resets the cached bootstrap data.
 * Forces the next call to getBootstrapData to re-parse the data from the DOM.
 * Useful for testing or when the bootstrap data has been updated dynamically.
 *
 * @returns {void}
 */
export const resetBootstrapDataCache = () => {
  data = undefined;
};

/**
 * Retrieves bootstrap data from the document body's data-bootstrap attribute.
 * The data is cached after the first access for performance.
 * Supports both gzip-compressed and plain JSON bootstrap data.
 *
 * @param {string|string[]} key - The key to retrieve from bootstrap data.
 *   Can be a string or array of strings (which will be joined with underscores).
 *   The key is automatically converted to snake_case before lookup.
 * @returns {*} The value associated with the key in the bootstrap data, or undefined if not found
 * @example
 * // Retrieve a simple key
 * const userId = getBootstrapData('userId'); // looks up 'user_id'
 *
 * @example
 * // Retrieve a nested key using array notation
 * const value = getBootstrapData(['user', 'settings', 'theme']); // looks up 'user_settings_theme'
 */
export default (key) => {
  const snakeCaseKey = getSnakeCaseKey(key);

  if (!data) {
    const { bootstrap } = document.body.dataset;

    if (bootstrap) {
      try {
        const decoded = decodeGzip(bootstrap);
        data = decoded ? JSON.parse(decoded) : JSON.parse(bootstrap);
      } catch {
        data = JSON.parse(bootstrap);
      }
    } else {
      data = {};
    }
  }

  return data?.[snakeCaseKey];
};
